import { ChangeBindPhone, VerifyPasswordCode, VerifyPhoneCode } from '@flowus/login';
import { VerifyEmailCode } from '@flowus/login/component/web-buildin/verify-code/email';
import { UserAuthorizationType } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useCallback } from 'react';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { useCurrentUser, useLogout } from 'src/hooks/user';
import { useFetchMe } from 'src/hooks/user/use-fetch-me';
import { SettingModalCommon } from '../common';
import { Button } from '@flowus/common/components';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

/** 换绑手机号 */
export const AccountPhone: FC = () => {
  const { t } = useAppTranslation();
  const currentUser = useCurrentUser();
  const openChangePhoneOrEmailDialog = useOpenChangePhoneOrEmailDialog();

  return (
    <SettingModalCommon.Item
      leftChild={
        <div className="space-y-0.5">
          <div>{t('common.phone')}</div>
          <div className="text-t4 text-grey3">{currentUser.phone}</div>
        </div>
      }
      rightChild={
        <Button onClick={() => openChangePhoneOrEmailDialog('phone')}>
          {t('views.settings.account.change_phone')}
        </Button>
      }
    />
  );
};

export const useOpenChangePhoneOrEmailDialog = () => {
  const { t } = useAppTranslation();
  const currentUser = useCurrentUser();
  const openModal = useOpenModal();
  const fetchMe = useFetchMe();
  const logout = useLogout();

  return useCallback(
    (type: 'phone' | 'email') => {
      const isPhone = type === 'phone';
      const openChangePhoneDialog = () => {
        openModal.warning({
          title: isPhone
            ? t('views.settings.account.confirm_change_phone')
            : t('views.settings.account.confirm_change_email'),
          confirmText: t('common.confirm'),
          cancelText: t('common.cancel'),
          minWidth: 400,
          content: (
            <div className="mb-10">
              <div>
                {isPhone
                  ? t('views.settings.account.after_change_phone')
                  : t('views.settings.account.after_change_email')}
              </div>
              <div>{isPhone ? currentUser.phone : currentUser.email}</div>
            </div>
          ),
          confirm: openSelectVerification,
        });
      };

      const openSelectVerification = () => {
        openModal.warning({
          title: t('views.settings.account.identity_verify'),
          confirmText: isPhone
            ? t('views.settings.account.verify_by_phone')
            : t('views.settings.account.verify_by_email'),
          cancelText: t('views.settings.account.verify_by_password'),
          colorType: 'active',
          minWidth: 400,
          content: (
            <div className="mb-10">
              <div className="mb-1.5">{t('views.settings.account.verify_method_desc')}</div>
              <div className="text-grey3 text-t4">{t('views.settings.account.verify_support')}</div>
            </div>
          ),
          confirm: () => {
            openVerifyCode();
          },
          cancel: () => {
            openVerifyPasswordCode();
          },
        });
      };

      const openVerifyCode = () => {
        openModal.modal({
          content: (_) =>
            isPhone ? (
              <VerifyPhoneCode
                phone={currentUser.phone}
                onSuccess={(code) => {
                  _.onCloseModal();
                  openBind(code, UserAuthorizationType.PHONE);
                }}
              />
            ) : (
              <VerifyEmailCode
                email={currentUser.email}
                onSuccess={(code) => {
                  _.onCloseModal();
                  openBind(code, UserAuthorizationType.EMAIL);
                }}
              />
            ),
        });
      };

      const openVerifyPasswordCode = () => {
        openModal.modal({
          content: (_) => (
            <VerifyPasswordCode
              userId={currentUser.uuid}
              onSuccess={(code) => {
                _.onCloseModal();
                openBind(
                  code,
                  isPhone ? UserAuthorizationType.PHONE : UserAuthorizationType.PASSWORD
                );
              }}
            />
          ),
        });
      };

      const openBind = (code: string, type: UserAuthorizationType) => {
        openModal.modal({
          content: (_) => (
            <ChangeBindPhone
              type={type}
              isEmail={!isPhone}
              authorizationCode={code}
              userId={currentUser.uuid}
              onSuccess={async () => {
                _.onCloseModal();
                await fetchMe();
                message.success(t('views.settings.account.login_expired'));
                void logout();
              }}
            />
          ),
        });
      };

      // 开始
      openChangePhoneDialog();
    },
    [currentUser.email, currentUser.phone, currentUser.uuid, fetchMe, logout, openModal, t]
  );
};
